import { stringCountLabel } from "@/lib/catalog/gauge-format";
import type { ConfiguratorReferenceData, ConfiguratorState } from "@/types/configurator";

// Resolves the human-readable summary of a configuration from reference data.
//
// Extracted from ReviewStep so the lookups are testable directly. They used to
// match on database ids while every step component wrote slugs, so a manual
// click-through rendered "—" for instrument, line and string type while only a
// URL-restored configuration read correctly. State stores slugs — see the
// contract in hooks/useConfiguratorUrlSync.ts.

export const UNKNOWN_LABEL = "—";

export interface ColorLabel {
  name: string;
  hexCode: string | null;
  swatchCss: string | null;
}

export interface SelectionLabels {
  instrument: string;
  /** Instrument plus count, as shown on one summary row. */
  instrumentAndCount: string;
  line: string;
  /** null when the line offers no string-type choice. */
  stringType: string | null;
  color: ColorLabel;
}

const UNKNOWN_COLOR: ColorLabel = { name: UNKNOWN_LABEL, hexCode: "#3A3A3A", swatchCss: null };

export function resolveSelectionLabels(
  state: Pick<ConfiguratorState, "instrumentId" | "lineId" | "stringTypeId" | "stringCount" | "colorId">,
  referenceData: ConfiguratorReferenceData,
): SelectionLabels {
  const instrument = referenceData.instruments.find((entry) => entry.slug === state.instrumentId);
  const line = referenceData.lines.find((entry) => entry.slug === state.lineId);
  const color = referenceData.lines
    .flatMap((entry) => entry.colors)
    .find((entry) => entry.slug === state.colorId);

  const instrumentName = instrument?.name ?? UNKNOWN_LABEL;
  return {
    instrument: instrumentName,
    instrumentAndCount: `${instrumentName} · ${stringCountLabel(state.stringCount ?? 0)}`,
    line: line?.name ?? UNKNOWN_LABEL,
    stringType: state.stringTypeId
      ? line?.stringTypes.find((type) => type.slug === state.stringTypeId)?.name ?? UNKNOWN_LABEL
      : null,
    color: color
      ? { name: color.name, hexCode: color.hexCode, swatchCss: color.swatchCss }
      : UNKNOWN_COLOR,
  };
}
